import { FormattedCaller } from 'components/fields/Submit'
import { CountryCode, isSupportedCountry, parsePhoneNumber } from 'libphonenumber-js'
import { isEmpty, isNil } from 'lodash'
import { Branch, Maybe, Participant, YesNo } from 'types/global'
import { TransformedTicket, TransformedVehicle } from 'types/ticket'
import { CommentType, HeroComments, HEROCustomer, HERORideEvent, HEROServiceEvent, HEROVehicle } from 'types/universalPayload'
import { splitName } from 'utils/splitName'
import { SoftService, Tow, TowExchange, Winch } from 'types/callstore'
import { AnyObject, LocationPayload, LocationStateLoad } from 'types/location'
import { isBranch, isLocation, isParticipant } from 'utils/typeChecks'
import { formatHours } from 'utils/formatHours'
import { generateDisablementNotes } from '../../utils/uly'
import { User } from '../../types/auth'
import { formatBranchHours } from 'utils/formatBranchHours'
import { VehicleConditions } from 'components/VehicleCondition'
import { YesNoTriggers } from 'components/Input'

export function generateServiceAdditionalData(service: any, ticket?: TransformedTicket, agent?: User, eventType?: string) {
  const attributes = {} as AnyObject
  attributes.ticketNumber = ticket?.ticketNumber ?? ticket?.reservationNumber
  attributes.groupBranchId = ticket?.pickupInfo.groupBranchId
  attributes.brand = ticket?.brandId
  attributes.isCustomVin = !!service?.customVin
  attributes.breakdownOption = service.breakdownOption
  attributes.agentId = agent?.nameID || ''
  attributes.customerSupplied = service.customerSupplied || false
  attributes.disablementReason = service.vehicleConditionSelect?.label || service.vehicleCondition || 'Other'
  attributes.unattended = service.unattended || false
  attributes.isRideAlong = service.rideAlong || false
  if ((eventType === 'tow' || eventType === 'tow exchange') && service.unattended && typeof service.keysLocation === 'object') {
    const keyLocation = service.keysLocation?.label
    const otherLocation = service.keysLocationOther
    let keyNotes = keyLocation
    if (otherLocation) {
      keyNotes = `Other: ${otherLocation}`
    }
    attributes.keyLocation = keyNotes || 'N/A'
  }
  if (eventType === 'lockout') attributes.keyLocation = service?.keysLocation || 'N/A'
  if (service.unattended && !attributes.keyLocation) attributes.keyLocation = 'N/A'
  attributes.newVehicleDelivery = !!(service.towDirection === 'branch' && service.disabledVehiclePickup)
  return attributes
}

export function generateVehicleAdditionalData(vehicle: TransformedVehicle) {
  const additionalData = {} as AnyObject
  additionalData.unitNumber = vehicle.unitNumber
  return additionalData
}

export function generateVehiclePayload(vehicle: Maybe<TransformedVehicle>, fuelType: string = 'GAS'): HEROVehicle {
  let STANDARD_VEHICLE_PAYLOAD: HEROVehicle = {
    id: '',
    make: 'N/A',
    model: 'N/A',
    year: new Date().getFullYear().toString(),
    color: '#000000',
    country: 'USA',
    vin: 'N/A',
    licensePlate: 'N/A',
    licensePlateState: 'N/A',
    fuelType: 'GAS',
  }

  if (isNil(vehicle)) {
    console.error('No Vehicle Found in generateVehiclePayload().')
    return STANDARD_VEHICLE_PAYLOAD
  }
  // if (isNil(vehicle.country)) {
  //   console.error('No Country Found in generateVehiclePayload().')
  //   return STANDARD_VEHICLE_PAYLOAD
  // }
  if (isNil(vehicle.licensePlate)) {
    console.error('No LicensePlate Found in generateVehiclePayload().')
    return STANDARD_VEHICLE_PAYLOAD
  }
  if (isNil(vehicle.licensePlateState)) {
    console.error('No LicensePlateState Found in generateVehiclePayload().')
    return STANDARD_VEHICLE_PAYLOAD
  }

  STANDARD_VEHICLE_PAYLOAD.id = vehicle.vin
  STANDARD_VEHICLE_PAYLOAD.make = vehicle.make
  STANDARD_VEHICLE_PAYLOAD.model = vehicle.model
  STANDARD_VEHICLE_PAYLOAD.year = String(vehicle.year)
  STANDARD_VEHICLE_PAYLOAD.color = vehicle.color
  STANDARD_VEHICLE_PAYLOAD.vin = vehicle.vin
  STANDARD_VEHICLE_PAYLOAD.licensePlate = vehicle.licensePlate
  STANDARD_VEHICLE_PAYLOAD.licensePlateState = vehicle.licensePlateState
  STANDARD_VEHICLE_PAYLOAD.country = vehicle.country
  STANDARD_VEHICLE_PAYLOAD.countrySubdivision = vehicle.countrySubdivision
  STANDARD_VEHICLE_PAYLOAD.fuelType = fuelType
  STANDARD_VEHICLE_PAYLOAD.additionalData = generateVehicleAdditionalData(vehicle)
  return STANDARD_VEHICLE_PAYLOAD
}

export function generateCustomerPayload(selectedDriver: FormattedCaller): HEROCustomer {
  let STANDARD_CUSTOMER_PAYLOAD: HEROCustomer = {
    name: '',
    phone: { number: '000 000 0000', country: 'US', smsOptIn: false },
  }

  if (isNil(selectedDriver) || isEmpty(selectedDriver)) {
    console.error('No SelectedDriver Found in generateCustomerPayload(<selectedDriver>).')
    return STANDARD_CUSTOMER_PAYLOAD
  }
  if (isEmpty(selectedDriver.countryCode) || isNil(selectedDriver.countryCode.label)) {
    console.error('No Phone Country Found in generateCustomerPayload().')
    return STANDARD_CUSTOMER_PAYLOAD
  }
  if (isEmpty(selectedDriver.number) || isNil(selectedDriver.number)) {
    console.error('No Phone Found in generateCustomerPayload().')
    return STANDARD_CUSTOMER_PAYLOAD
  }

  const { firstName, lastName } = splitName(selectedDriver.callerName)

  let defaultCountry = selectedDriver.countryCode.label.toString().toUpperCase() as CountryCode
  if (!isSupportedCountry(defaultCountry)) defaultCountry = 'US'
  const phoneNumber = parsePhoneNumber(selectedDriver.number, defaultCountry)

  STANDARD_CUSTOMER_PAYLOAD.name = `${firstName} ${lastName}`
  STANDARD_CUSTOMER_PAYLOAD.phone.number = phoneNumber?.number || selectedDriver.number
  STANDARD_CUSTOMER_PAYLOAD.phone.country = defaultCountry
  STANDARD_CUSTOMER_PAYLOAD.phone.smsOptIn = selectedDriver.optIn || false

  return STANDARD_CUSTOMER_PAYLOAD
}

export function generateServiceStatus() {
  return {
    code: 'CR',
    description: 'Created',
    dateCreated: new Date().toISOString(),
    modifiedDate: null,
  }
}

export function generateProviderStatus() {
  return {
    eta: null,
    status: null,
    modifiedDate: null,
    attributes: null,
  }
}

export function generateServiceLocation(location: LocationStateLoad, timezone?: string | null) {
  // Vehicle / Caller Location
  if (isLocation(location)) {
    const breakdownLocation = location as LocationPayload

    const { postalCode: zip, ...locationAddress } = breakdownLocation.address
    const latitude = breakdownLocation.latitude
    const longitude = breakdownLocation.longitude
    const address = { ...locationAddress, zip }
    const locationName = ''
    const countrySubdivision = breakdownLocation.address.state
    const fullAddressComposed = `${locationAddress.streetNumber} ${locationAddress.street}, ${locationAddress.city}, ${locationAddress.state} ${zip}, ${locationAddress.country}`
    const addressFull = breakdownLocation.addressDescription ?? fullAddressComposed ?? ''
    return { latitude, longitude, locationName, timezone, address: { ...address, addressFull, countrySubdivision } }
  }

  // Rental Branch
  if (isBranch(location)) {
    const branchLocation = location as Branch
    const { street_addresses, city, postal, country_code, country_subdivision_code: state } = branchLocation.address
    const latitude = branchLocation.gps.latitude
    const longitude = branchLocation.gps.longitude
    const [houseNumber, ...street] = street_addresses && street_addresses[0].split(' ')
    const address = {
      streetNumber: houseNumber,
      street: street.join(' '),
      city: city ?? '',
      state: state ?? '',
      zip: postal,
      country: country_code,
    }
    const locationName = branchLocation.name
    const countrySubdivision = state
    const fullAddressComposed = `${street_addresses[0]} ${city}, ${state}, ${postal}, ${country_code}`
    const addressFull = fullAddressComposed ?? ''
    return { latitude, longitude, locationName, timezone, address: { ...address, addressFull, countrySubdivision } }
  }

  // Tow Destination / Network
  if (isParticipant(location)) {
    const participantLocation = location as Participant & LocationPayload
    const { addressLine1, city, administrativeArea, postalCode, country } = participantLocation.entity.address!
    const { latitude, longitude } = participantLocation.location!
    const [houseNumber, ...street] = (addressLine1 && addressLine1.split(' ')) ?? ''
    const address = {
      streetNumber: houseNumber,
      street: street.join(' '),
      city: city ?? '',
      state: administrativeArea ?? '',
      zip: postalCode ?? '',
      country: country ?? '',
    }
    const locationName = participantLocation.entity?.name || ''
    const countrySubdivision = administrativeArea
    const fullAddressComposed = `${addressLine1} ${city}, ${administrativeArea}, ${postalCode}, ${country}`
    const addressFull = fullAddressComposed ?? ''
    return { latitude, longitude, locationName, timezone, address: { ...address, addressFull, countrySubdivision } }
  }

  return undefined
}

export function generateServicePayload(
  service: any,
  eventType?: string,
  ticket?: TransformedTicket,
  timezone?: string | null,
  agent?: User,
): HEROServiceEvent {
  const breakdownLocation = (service.serviceLocations.vehicleLocation ?? service.serviceLocations.callerLocation)!
  const callerLocation = (service.serviceLocations.callerLocation ?? service.serviceLocations.vehicleLocation)!
  const exchangeLocation = service.serviceLocations.exchangeLocation!
  const vehicleDestination = (service.serviceLocations.vehicleDestinationLocation ??
    service.serviceLocations.callerDestinationLocation)!
  const newVehicleDelivery = !!(service.towDirection === 'branch' && service.disabledVehiclePickup)

  if (
    service.type === 'towExchange' ||
    !isEmpty(service.towDirection) ||
    (service.serviceLocations.exchangeLocation && service.serviceLocations.vehicleLocation)
  )
    eventType = 'tow exchange'

  if (service.type === 'towExchange' && !newVehicleDelivery) eventType = 'tow'

  if (eventType?.includes('tires')) eventType = 'Tires'
  const scheduledAt = (service as any)?.scheduledAt

  return {
    requestedService: eventType || '',
    ...(typeof scheduledAt === 'string' && scheduledAt.trim() !== '' ? { scheduledAt } : {}),
    serviceStatus: generateServiceStatus(),
    provider: generateProviderStatus(),
    serviceLocations: {
      breakdownLocation: generateServiceLocation(breakdownLocation, timezone),
      callerLocation: generateServiceLocation(callerLocation, timezone),
      ...(exchangeLocation && newVehicleDelivery
        ? { exchangeLocation: generateServiceLocation(exchangeLocation, timezone) }
        : {}),
      ...(vehicleDestination ? { vehicleDestination: generateServiceLocation(vehicleDestination, timezone) } : {}),
      additionalData: generateServiceAdditionalData(service, ticket, agent, eventType),
    },
  }
}

export function generateCommentsPayload(
  service: SoftService | Tow | TowExchange,
  hasSpareTire?: any,
  eventType?: any,
  branchHourData?: any,
): HeroComments[] {
  const comments = [] as HeroComments[]
  const { customerLocation, vehicleConditionSelect } = service as Tow | TowExchange
  const { towDirection } = service as TowExchange
  const winchService = service as Winch

  const lockoutNotes = {
    type: CommentType.STANDARD,
    text: 'Please perform Lockout service with Tow.',
    dateCreated: new Date().toISOString(),
  }

  const winchNotes = {
    type: CommentType.STANDARD,
    text: 'Please perform Winch service with Tow.',
    dateCreated: new Date().toISOString(),
  }

  const unattendedNotes = {
    type: CommentType.STANDARD,
    text: 'Vehicle is unattended and locked.',
    dateCreated: new Date().toISOString(),
  }

  const breakdownNote = 'Vehicle Breakdown: ' + service.breakdownOption + '. '
  const exchangeNote =
    service.serviceLocations.exchangeLocation &&
    service.serviceLocations.vehicleDestinationLocation &&
    !isEmpty(towDirection) &&
    towDirection === 'branch'
      ? 'This request is part of a tow exchange. '
      : ''
  const notesToProvider = 'Notes to Provider: ' + service.notes
  const photosRequiredNote = '. PHOTOS REQUIRED.'
  const gpsNotes = ' USE GPS COORDINATES FOR ACCURATE SERVICE LOCATION'
  const serviceNotes = breakdownNote + exchangeNote + notesToProvider + photosRequiredNote + gpsNotes
  const standardNotes = {
    type: CommentType.PROVIDER,
    text: serviceNotes || 'No service notes available',
    dateCreated: new Date().toISOString(),
  }

  const getDisablementNotes = generateDisablementNotes(eventType, service, hasSpareTire)
  const disablementNotes = {
    type: CommentType.STANDARD,
    text: getDisablementNotes,
    dateCreated: new Date().toISOString(),
  }

  const formattedVehicleDestinationHours =
    formatHours(service.serviceLocations?.vehicleDestinationLocation as Participant)! || 'Destination hours unavailable.'
  const vehicleDestinationHours = {
    type: CommentType.STANDARD,
    text: 'Vehicle Destination Hours: ' + formattedVehicleDestinationHours,
    dateCreated: new Date().toISOString(),
  }

  const formattedExchangeHours = formatBranchHours(branchHourData)

  let callerDestinationHoursString
  if (customerLocation && customerLocation === 'exchangeBranchDropoff') {
    callerDestinationHoursString = formattedExchangeHours
  } else if (customerLocation && customerLocation === 'vehicleDestDropoff') {
    callerDestinationHoursString = formattedVehicleDestinationHours
  }

  const callerDestinationHours = {
    type: CommentType.STANDARD,
    text: 'Caller Destination Hours: ' + callerDestinationHoursString,
    dateCreated: new Date().toISOString(),
  }

  // Standard Notes To Provider
  if (!isEmpty(standardNotes.text)) comments.push(standardNotes)
  if (!isEmpty(disablementNotes.text)) comments.push(disablementNotes)
  if (service.serviceLocations?.vehicleDestinationLocation) comments.push(vehicleDestinationHours)
  if (service.serviceLocations?.callerDestinationLocation) comments.push(callerDestinationHours)

  // Additional Provider Notes
  if ((service as Tow | TowExchange).unattended) comments.push(unattendedNotes)
  if (
    vehicleConditionSelect?.value === VehicleConditions.LOST_KEY ||
    vehicleConditionSelect?.value === VehicleConditions.KEYS_LOCKED
  )
    comments.push(lockoutNotes)
  if (winchService.okayToDrive === YesNoTriggers.NO && winchService.moreThan50ft === YesNoTriggers.NO) comments.push(winchNotes)

  // Add after-hours drop instructions note (use N/A if instructions are not provided)
  const afterHoursDrop = service.serviceLocations?.vehicleDestinationLocation?.properties?.afterHoursDrop
  if (afterHoursDrop?.toUpperCase() === YesNo.YES) {
    const afterHoursDropInstructions =
      service.serviceLocations?.vehicleDestinationLocation?.properties?.afterHoursDropInstructions
    const dropInstructions = {
      type: CommentType.PROVIDER,
      text: `After Hours Drop Accepted, Drop Instructions: ${afterHoursDropInstructions ?? 'N/A'}`,
      dateCreated: new Date().toISOString(),
    }
    comments.push(dropInstructions)
  }

  return comments
}

export function generateRidePayload(service: any, timezone?: string | null): HERORideEvent {
  const callerLocation = service.serviceLocations.callerLocation!
  const { customerLocation, passengerCount, luggageCount, notes } = service as Tow | TowExchange
  const rideNotes = (notes ?? '') + (service?.accommodationNotes ?? '')

  let destinationPrefix = ''
  if (customerLocation && customerLocation === 'exchangeBranchDropoff') {
    destinationPrefix = 'EXCHANGE BRANCH - '
  } else if (customerLocation && customerLocation === 'vehicleDestDropoff') {
    destinationPrefix = 'VEHICLE DESTINATION - '
  }
  const callerDestination = generateServiceLocation(service.serviceLocations.callerDestinationLocation, timezone)
  const callerDestinationLocation = {
    ...callerDestination,
    locationName: destinationPrefix + callerDestination?.locationName,
  }

  return {
    passengerCount: passengerCount?.value ?? null,
    luggageCount: luggageCount?.value ?? null,
    notes: rideNotes,
    callerPickupLocation: generateServiceLocation(callerLocation, timezone),
    //@ts-ignore
    callerDestination: callerDestinationLocation,
  }
}
